import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../components/shared/AuthContext';
import { PageLayout, Table, Button, DatePicker, LogoLoader } from "../../components/ui";
import ReportHeader from "../../components/reports/ReportHeader";
import * as XLSX from 'xlsx';
// amountToRelease / gst / processingCharge store PAISE.
import { toRupees } from "../../utils/money";

const SavingsReport = () => {
  const [transactions, setTransactions] = useState([]);
  const [proposedLoans, setProposedLoans] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [errorTransactions, setErrorTransactions] = useState(null);
  const [errorLoans, setErrorLoans] = useState(null);

  const [transactionType, setTransactionType] = useState('All');
  const [cashType, setCashType] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loanFilterType, setLoanFilterType] = useState('all');
  const [loanDateFrom, setLoanDateFrom] = useState('');
  const [loanDateTo, setLoanDateTo] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Savings Check State
  const [savingsCustomers, setSavingsCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [loadingSavings, setLoadingSavings] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [savingsSearch, setSavingsSearch] = useState('');
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [exporting, setExporting] = useState(false);

  const [allBranches, setAllBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');

  const { data, roleName } = useAuth();
  const branchId = data?.branchId || null;
  const isSuperAdmin = roleName === 'superadmin';
  const canViewAllBranches = isSuperAdmin;

  useEffect(() => {
    if (!canViewAllBranches && branchId) {
      setSelectedBranch(branchId);
    }
  }, [canViewAllBranches, branchId]);

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getWeekStartDate = () => {
    const date = new Date();
    const day = date.getDay();
    const diff = date.getDate() - day;
    return new Date(date.setDate(diff)).toISOString().split('T')[0];
  };

  const getMonthStartDate = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
  };

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/getModelData/branch_details`);
        setAllBranches(response.data.data || []);
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };

    if (canViewAllBranches) {
      fetchBranches();
    }
  }, [canViewAllBranches]);

  const fetchTransactions = async () => {
    setLoadingTransactions(true);
    setErrorTransactions(null);
    try {
      let params = {};

      if (!canViewAllBranches) {
        params.branchId = branchId;
      }
      else if (selectedBranch) {
        params.branchId = selectedBranch;
      }

      if (dateFrom) {
        params.fromDate = dateFrom;
      }
      if (dateTo) {
        params.toDate = dateTo;
      }

      const response = await axios.get(`${API_BASE_URL}/api/getModelData/transactions`, {
        params: params,
      });

      let records = Array.isArray(response.data?.data) ? response.data.data : [];

      // Filter by transaction type and cash type
      records = records.filter(record => {
        if (transactionType === 'All') {
          if (record.transactionType?.toLowerCase() === 'withdraw') {
            return true;
          }
          if (record.transactionType?.toLowerCase() === 'deposit') {
            if (cashType === 'All') {
              return ['cash', 'online', 'electronic'].includes(record.cash_type?.toLowerCase());
            }
            return record.cash_type?.toLowerCase() === cashType.toLowerCase();
          }
          return false;
        }
        if (record.transactionType?.toLowerCase() === transactionType.toLowerCase()) {
          if (transactionType.toLowerCase() === 'withdraw') {
            return true;
          }
          if (transactionType.toLowerCase() === 'deposit') {
            if (cashType === 'All') {
              return ['cash', 'online', 'electronic'].includes(record.cash_type?.toLowerCase());
            }
            return record.cash_type?.toLowerCase() === cashType.toLowerCase();
          }
        }
        return false;
      });

      setTransactions(records);
    } catch (err) {
      console.error('Error fetching transactions:', err.response?.data || err.message);
      setErrorTransactions('Failed to load transaction records. Please check the server logs.');
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchProposedLoans = async () => {
    setLoadingLoans(true);
    setErrorLoans(null);
    try {
      let params = {
        includeDepth: 1
      };

      if (!canViewAllBranches) {
        params.branchId = branchId;
      }
      else if (selectedBranch) {
        params.branchId = selectedBranch;
      }

      // Calculate date range based on filter type
      if (loanFilterType !== 'all') {
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];

        if (loanFilterType === 'daily') {
          params.fromDate = todayStr;
          params.toDate = todayStr;
        } else if (loanFilterType === 'weekly') {
          const weekStart = new Date();
          weekStart.setDate(weekStart.getDate() - weekStart.getDay());
          params.fromDate = weekStart.toISOString().split('T')[0];
          params.toDate = todayStr;
        } else if (loanFilterType === 'monthly') {
          const monthStart = new Date();
          monthStart.setDate(1);
          params.fromDate = monthStart.toISOString().split('T')[0];
          params.toDate = todayStr;
        } else if (loanFilterType === 'dateRange') {
          if (loanDateFrom) params.fromDate = loanDateFrom;
          if (loanDateTo) params.toDate = loanDateTo;
        }
      }

      // Fetch member_details (with date filtering)
      const memberResponse = await axios.get(`${API_BASE_URL}/api/getModelData/member_details`, {
        params: params
      });

      // Fetch proposed_loan_details
      const loanResponse = await axios.get(`${API_BASE_URL}/api/getModelData/proposed_loan_details`, {
        params: {
          includeDepth: 1
        }
      });

      let memberRecords = Array.isArray(memberResponse.data?.data) ? memberResponse.data.data : [];
      let loanRecords = Array.isArray(loanResponse.data?.data) ? loanResponse.data.data : [];

      // Merge data based on memberId
      let records = memberRecords
        .map(member => {
          const loan = loanRecords.find(l => l.memberId === member.id) || {};

          return {
            id: member.id || 'N/A',
            applicationId: member.ApplicationId || 'N/A',
            applicantName: loan.applicantName || 'N/A',
            tenureInMonths: loan.tenureInMonths || 0,
            processingCharge: member.processingCharge || 0,
            gst: member.gst || 0,
            // PAISE -> RUPEES. Sits alongside savings balances drawn from
            // transactions, which are still rupees.
            loanAmount: toRupees(member.amountToRelease),
            loanType: member.loanType || 'N/A',
            createdAt: member.createdAt || loan.createdAt,
            branchId: member.branchId
          };
        });

      setProposedLoans(records);
    } catch (err) {
      console.error('Error fetching proposed loans:', err.response?.data || err.message);
      setErrorLoans('Failed to load proposed loan records. Please check the server logs.');
      setProposedLoans([]);
    } finally {
      setLoadingLoans(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [branchId, selectedBranch, transactionType, cashType, dateFrom, dateTo]);

  useEffect(() => {
    fetchProposedLoans();
  }, [branchId, selectedBranch, loanFilterType, loanDateFrom, loanDateTo]);

  const fetchSavingsCustomers = async () => {
    setLoadingSavings(true);
    try {
      let bId = !canViewAllBranches ? branchId : selectedBranch;

      if (!bId) {
        setSavingsCustomers([]);
        setTotalCustomers(0);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/v2/savings-check/customers`, {
        params: {
          branchId: bId,
          page: page + 1, // API might be 1-indexed, or let's keep it matching original logic
          limit: rowsPerPage,
          search: savingsSearch
        }
      });
      setSavingsCustomers(response.data.data || []);
      setTotalCustomers(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching savings customers:', error);
    } finally {
      setLoadingSavings(false);
    }
  };

  const fetchCustomerHistory = async (customer) => {
    setLoadingHistory(true);
    setSelectedCustomer(customer);
    setHistoryDialogOpen(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v2/savings-check/history/${customer.customerUniqueId}`);
      setCustomerHistory(response.data.data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      setCustomerHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (tabValue === 2) {
        if (page !== 0) {
          setPage(0);
        } else {
          fetchSavingsCustomers();
        }
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [savingsSearch]);

  useEffect(() => {
    if (tabValue === 2) {
      fetchSavingsCustomers();
    }
  }, [tabValue, branchId, selectedBranch, page, rowsPerPage]);

  const handleExport = async () => {
    setExporting(true);
    try {
      let bId = !canViewAllBranches ? branchId : selectedBranch;

      if (!bId) {
        return;
      }

      const params = new URLSearchParams({
        branchId: bId,
        search: savingsSearch || ''
      });

      const downloadUrl = `${API_BASE_URL}/api/v2/savings-check/export?${params.toString()}`;

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `Savings_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export failed", error);
    } finally {
      setTimeout(() => setExporting(false), 2000);
    }
  };

  const calculateTransactionTotals = () => {
    if (!Array.isArray(transactions)) return { deposits: 0, withdrawals: 0, total: 0, count: 0 };

    return transactions.reduce((acc, transaction) => {
      const amount = Number(transaction.amount) || 0;
      acc.count += 1;

      if (transaction.transactionType?.toLowerCase() === 'deposit') {
        acc.deposits += amount;
      } else if (transaction.transactionType?.toLowerCase() === 'withdraw') {
        acc.withdrawals += amount;
      }

      return acc;
    }, { deposits: 0, withdrawals: 0, count: 0 });
  };

  const calculateLoanTotals = () => {
    if (!Array.isArray(proposedLoans)) return {
      processingCharge: 0,
      gst: 0,
      totalLoanAmount: 0,
      count: 0
    };

    return proposedLoans.reduce((acc, loan) => {
      acc.processingCharge += Number(loan.processingCharge) || 0;
      acc.gst += Number(loan.gst) || 0;
      acc.totalLoanAmount += Number(loan.loanAmount) || 0;
      acc.count += 1;
      return acc;
    }, {
      processingCharge: 0,
      gst: 0,
      totalLoanAmount: 0,
      count: 0
    });
  };

  const transactionTotals = calculateTransactionTotals();
  const loanTotals = calculateLoanTotals();
  const netAmount = transactionTotals.deposits - transactionTotals.withdrawals;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY
  };

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      {/* Tabs */}
      <div className="flex flex-row flex-wrap items-center gap-2 self-start mb-4">
        {[
          { id: 0, label: 'Transactions Report', icon: 'fa-wallet' },
          { id: 1, label: 'Proposed Loans Report', icon: 'fa-file-invoice-dollar' },
          { id: 2, label: 'Savings Check Options', icon: 'fa-piggy-bank' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setTabValue(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
              tabValue === tab.id
                ? 'bg-primary text-white shadow-sm'
                : 'text-ink-600 hover:text-ink-900 hover:bg-canvas-muted'
            }`}
          >
            <i className={`fa-solid ${tab.icon}`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ReportHeader with Filters and Actions */}
      <ReportHeader
        className="mb-4"
        filters={
          <>
            {tabValue === 0 && (
              <>
                {/* Transaction Type */}
                <div className="relative min-w-[150px] mt-1">
                  <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                    Transaction Type
                  </label>
                  <div className="relative">
                    <select
                      value={transactionType}
                      onChange={(e) => setTransactionType(e.target.value)}
                      className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                    >
                      <option value="All">All Transactions</option>
                      <option value="Deposit">Deposits</option>
                      <option value="Withdraw">Withdrawals</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Cash Category */}
                <div className="relative min-w-[150px] mt-1">
                  <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                    Cash Category
                  </label>
                  <div className="relative">
                    <select
                      value={cashType}
                      onChange={(e) => setCashType(e.target.value)}
                      disabled={transactionType === 'Withdraw'}
                      className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium disabled:bg-canvas-muted disabled:text-ink-300 disabled:cursor-not-allowed"
                    >
                      <option value="All">All Categories</option>
                      <option value="cash">Cash</option>
                      <option value="online">Online</option>
                      <option value="electronic">Electronic</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Start Date */}
                <div className="w-[145px] mt-1">
                  <DatePicker
                    label="Start Date"
                    selected={dateFrom}
                    onChange={(date) => setDateFrom(date ? date.toISOString().split('T')[0] : '')}
                    placeholder="DD-MM-YYYY"
                  />
                </div>

                {/* End Date */}
                <div className="w-[145px] mt-1">
                  <DatePicker
                    label="End Date"
                    selected={dateTo}
                    onChange={(date) => setDateTo(date ? date.toISOString().split('T')[0] : '')}
                    placeholder="DD-MM-YYYY"
                  />
                </div>
              </>
            )}

            {tabValue === 1 && (
              <>
                {/* Filter Type */}
                <div className="relative min-w-[160px] mt-1">
                  <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                    Filter Type
                  </label>
                  <div className="relative">
                    <select
                      value={loanFilterType}
                      onChange={(e) => setLoanFilterType(e.target.value)}
                      className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                    >
                      <option value="all">All Time</option>
                      <option value="daily">Today</option>
                      <option value="weekly">This Week</option>
                      <option value="monthly">This Month</option>
                      <option value="dateRange">Date Range</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {loanFilterType === 'dateRange' && (
                  <>
                    {/* From Date */}
                    <div className="w-[145px] mt-1">
                      <DatePicker
                        label="From Date"
                        selected={loanDateFrom}
                        onChange={(date) => setLoanDateFrom(date ? date.toISOString().split('T')[0] : '')}
                        placeholder="DD-MM-YYYY"
                      />
                    </div>

                    {/* To Date */}
                    <div className="w-[145px] mt-1">
                      <DatePicker
                        label="To Date"
                        selected={loanDateTo}
                        onChange={(date) => setLoanDateTo(date ? date.toISOString().split('T')[0] : '')}
                        placeholder="DD-MM-YYYY"
                      />
                    </div>
                  </>
                )}

                <div className="flex flex-col justify-end">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setLoanFilterType('all');
                      setLoanDateFrom('');
                      setLoanDateTo('');
                    }}
                    className="h-[38px] px-4"
                  >
                    <i className="fa-solid fa-trash-can mr-1.5"></i>
                    Clear
                  </Button>
                </div>
              </>
            )}

            {tabValue === 2 && (
              <div className="flex flex-col gap-1.5 min-w-[280px]">
                <label className="text-[10px] font-bold text-ink-500 uppercase tracking-wider">Search Customers</label>
                <div className="relative">
                  <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 text-sm pointer-events-none"></i>
                  <input
                    type="text"
                    placeholder="Search by Customer Name or ID..."
                    value={savingsSearch}
                    onChange={(e) => setSavingsSearch(e.target.value)}
                    style={{ paddingLeft: "2.5rem" }}
                    className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[38px] text-sm !pl-10 pr-3 focus:outline-none transition-all w-full"
                  />
                </div>
              </div>
            )}
          </>
        }
        actions={
          <div className="flex items-center gap-3">
            {canViewAllBranches && (
              <div className="flex flex-col gap-1 min-w-[180px]">
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[38px] text-xs px-3 focus:outline-none transition-all font-semibold uppercase tracking-wider"
                >
                  <option value="">Global (All Branches)</option>
                  {allBranches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.branchName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {tabValue === 2 && (
              <Button
                onClick={handleExport}
                disabled={exporting || savingsCustomers.length === 0}
                variant="primary"
                size="sm"
                className="shadow-sm"
              >
                {exporting ? (
                  <>
                    <LogoLoader size="xs" className="inline-flex mr-1.5 p-0" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-file-excel mr-1.5"></i>
                    Export to Excel
                  </>
                )}
              </Button>
            )}
          </div>
        }
      />
      {tabValue === 0 && (
        <div className="flex-grow flex flex-col min-h-0">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Total Deposits */}
            <div className="bg-[#DDF5E6] border border-[#16A34A]/25 py-2.5 px-4 rounded-xl shadow-xs flex flex-col justify-center relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-[#16A34A] uppercase tracking-wider">Total Deposits</span>
                <div className="w-6 h-6 rounded-md bg-white/80 text-[#16A34A] text-xs flex items-center justify-center shadow-xs">
                  <i className="fa-solid fa-arrow-trend-up"></i>
                </div>
              </div>
              <span className="text-xl font-black text-[#16A34A] leading-tight">
                ₹{transactionTotals.deposits.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>

            {/* Total Withdrawals */}
            <div className="bg-[#E9DFFF] border border-[#7C3AED]/25 py-2.5 px-4 rounded-xl shadow-xs flex flex-col justify-center relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-[#7C3AED] uppercase tracking-wider">Total Withdrawals</span>
                <div className="w-6 h-6 rounded-md bg-white/80 text-[#7C3AED] text-xs flex items-center justify-center shadow-xs">
                  <i className="fa-solid fa-arrow-trend-down"></i>
                </div>
              </div>
              <span className="text-xl font-black text-[#7C3AED] leading-tight">
                ₹{transactionTotals.withdrawals.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>

            {/* Net Amount */}
            <div className="bg-[#D9F3F7] border border-[#0891B2]/25 py-2.5 px-4 rounded-xl shadow-xs flex flex-col justify-center relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-[#0891B2] uppercase tracking-wider">Net Amount</span>
                <div className="w-6 h-6 rounded-md bg-white/80 text-[#0891B2] text-xs flex items-center justify-center shadow-xs">
                  <i className="fa-solid fa-scale-balanced"></i>
                </div>
              </div>
              <span className="text-xl font-black text-[#0891B2] leading-tight">
                ₹{netAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>

            {/* Total Transactions */}
            <div className="bg-[#FFF0C7] border border-[#D97706]/25 py-2.5 px-4 rounded-xl shadow-xs flex flex-col justify-center relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-[#D97706] uppercase tracking-wider">Total Transactions</span>
                <div className="w-6 h-6 rounded-md bg-white/80 text-[#D97706] text-xs flex items-center justify-center shadow-xs">
                  <i className="fa-solid fa-list-check"></i>
                </div>
              </div>
              <span className="text-xl font-black text-[#D97706] leading-tight">
                {transactionTotals.count}
              </span>
            </div>
          </div>

          {/* Transactions Table */}
          {loadingTransactions ? (
            <div className="flex justify-center py-8">
              <LogoLoader size="md" />
            </div>
          ) : errorTransactions ? (
            <div className="bg-rose-50 text-rose-700 p-4 rounded-xl border border-rose-200 text-sm font-medium">
              {errorTransactions}
            </div>
          ) : (
            <Table>
              <Table.Head>
                <tr>
                  <Table.HeadCell align="center">Date</Table.HeadCell>
                  <Table.HeadCell align="right">Amount</Table.HeadCell>
                  <Table.HeadCell align="center">Type</Table.HeadCell>
                  <Table.HeadCell align="center">Cash Type</Table.HeadCell>
                </tr>
              </Table.Head>
              <Table.Body>
                {transactions.length > 0 ? (
                  transactions.map((transaction, index) => (
                    <Table.Row key={transaction.id || index}>
                      <Table.Cell align="center">{formatDate(transaction.date)}</Table.Cell>
                      <Table.Cell align="right" className="font-semibold text-ink-800">
                        ₹{(transaction.amount || 0).toFixed(2)}
                      </Table.Cell>
                      <Table.Cell align="center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                          transaction.transactionType?.toLowerCase() === 'deposit'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}>
                          {transaction.transactionType || 'N/A'}
                        </span>
                      </Table.Cell>
                      <Table.Cell align="center" className="text-ink-600 font-medium">
                        {transaction.cash_type || 'N/A'}
                      </Table.Cell>
                    </Table.Row>
                  ))
                ) : (
                  <Table.EmptyRow colSpan={4} message="No transactions found" />
                )}
              </Table.Body>
            </Table>
          )}
        </div>
      )}

      {tabValue === 1 && (
        <div className="flex-grow flex flex-col min-h-0">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Portfolio Value */}
            <div className="bg-[#E9DFFF] border border-[#7C3AED]/25 py-2.5 px-4 rounded-xl shadow-xs flex flex-col justify-center relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-[#7C3AED] uppercase tracking-wider">Portfolio Value</span>
                <div className="w-8 h-8 rounded-lg bg-white/80 text-[#7C3AED] text-sm flex items-center justify-center shadow-xs">
                  <i className="fa-solid fa-vault"></i>
                </div>
              </div>
              <span className="text-xl font-black text-[#7C3AED] leading-tight">
                ₹{loanTotals.totalLoanAmount.toLocaleString()}
              </span>
            </div>

            {/* Operational Charges */}
            <div className="bg-[#FFF0C7] border border-[#D97706]/25 py-2.5 px-4 rounded-xl shadow-xs flex flex-col justify-center relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-[#D97706] uppercase tracking-wider">Operational Charges</span>
                <div className="w-8 h-8 rounded-lg bg-white/80 text-[#D97706] text-sm flex items-center justify-center shadow-xs">
                  <i className="fa-solid fa-hand-holding-dollar"></i>
                </div>
              </div>
              <span className="text-xl font-black text-[#D97706] leading-tight">
                ₹{(loanTotals.processingCharge + loanTotals.gst).toLocaleString()}
              </span>
            </div>

            {/* Active Proposals */}
            <div className="bg-[#DCEBFF] border border-[#2563EB]/25 py-2.5 px-4 rounded-xl shadow-xs flex flex-col justify-center relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-[#2563EB] uppercase tracking-wider">Active Proposals</span>
                <div className="w-8 h-8 rounded-lg bg-white/80 text-[#2563EB] text-sm flex items-center justify-center shadow-xs">
                  <i className="fa-solid fa-file-signature"></i>
                </div>
              </div>
              <span className="text-xl font-black text-[#2563EB] leading-tight">
                {loanTotals.count}
              </span>
            </div>
          </div>

          {/* Proposed Loans Table */}
          {loadingLoans ? (
            <div className="flex justify-center py-8">
              <LogoLoader size="md" />
            </div>
          ) : errorLoans ? (
            <div className="bg-rose-50 text-rose-700 p-4 rounded-xl border border-rose-200 text-sm font-medium">
              {errorLoans}
            </div>
          ) : (
            <Table>
              <Table.Head>
                <tr>
                  <Table.HeadCell align="center">Application ID</Table.HeadCell>
                  <Table.HeadCell align="left">Applicant</Table.HeadCell>
                  <Table.HeadCell align="left">Loan Type</Table.HeadCell>
                  <Table.HeadCell align="right">Total Loan Amount</Table.HeadCell>
                  <Table.HeadCell align="center">Tenure (Months)</Table.HeadCell>
                  <Table.HeadCell align="right">Processing Charge</Table.HeadCell>
                  <Table.HeadCell align="right">GST</Table.HeadCell>
                </tr>
              </Table.Head>
              <Table.Body>
                {proposedLoans.length > 0 ? (
                  proposedLoans.map((loan, index) => (
                    <Table.Row key={loan.id || index}>
                      <Table.Cell align="center" className="font-semibold text-primary">{loan.applicationId}</Table.Cell>
                      <Table.Cell align="left" className="font-medium text-ink-800">{loan.applicantName}</Table.Cell>
                      <Table.Cell align="left">{loan.loanType}</Table.Cell>
                      <Table.Cell align="right" className="font-bold text-emerald-600">
                        ₹{Number(loan.loanAmount).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell align="center">{loan.tenureInMonths}</Table.Cell>
                      <Table.Cell align="right">₹{Number(loan.processingCharge).toLocaleString()}</Table.Cell>
                      <Table.Cell align="right">₹{Number(loan.gst).toLocaleString()}</Table.Cell>
                    </Table.Row>
                  ))
                ) : (
                  <Table.EmptyRow colSpan={7} message="No proposed loans found" />
                )}
              </Table.Body>
            </Table>
          )}
        </div>
      )}

      {tabValue === 2 && (
        <div className="flex-grow flex flex-col min-h-0">
          <Table>
            <Table.Head>
              <tr>
                <Table.HeadCell align="left">Customer Name</Table.HeadCell>
                <Table.HeadCell align="center">Customer ID</Table.HeadCell>
                <Table.HeadCell align="center">Last Transaction</Table.HeadCell>
                <Table.HeadCell align="right">Balance</Table.HeadCell>
                <Table.HeadCell align="center">Actions</Table.HeadCell>
              </tr>
            </Table.Head>
            <Table.Body>
              {loadingSavings ? (
                <Table.Row>
                  <Table.Cell colSpan={5} align="center">
                    <div className="flex justify-center py-4">
                      <LogoLoader size="md" />
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : (
                savingsCustomers.map((customer, index) => (
                  <Table.Row
                    key={index}
                    onClick={() => fetchCustomerHistory(customer)}
                    className="cursor-pointer"
                  >
                    <Table.Cell align="left" className="font-semibold text-ink-800">{customer.name}</Table.Cell>
                    <Table.Cell align="center" className="font-medium text-primary">{customer.customerUniqueId}</Table.Cell>
                    <Table.Cell align="center">{formatDate(customer.lastTransactionDate)}</Table.Cell>
                    <Table.Cell align="right" className={`font-bold ${Number(customer.balance) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      ₹{Number(customer.balance).toFixed(2)}
                    </Table.Cell>
                    <Table.Cell align="center">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchCustomerHistory(customer);
                        }}
                      >
                        <i className="fa-solid fa-eye mr-1.5"></i>
                        View History
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
              {!loadingSavings && savingsCustomers.length === 0 && (
                <Table.EmptyRow colSpan={5} message="No customers found" />
              )}
            </Table.Body>
          </Table>

          {/* Pagination Controls */}
          {totalCustomers > 0 && (
            <div className="flex items-center justify-between mt-4 bg-white p-3 rounded-xl border border-border-light shadow-sm">
              <div className="flex items-center gap-2 text-xs text-ink-500">
                <span>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  className="bg-transparent border border-border-medium rounded px-1.5 py-1 focus:outline-none font-semibold text-ink-700"
                >
                  {[10, 15, 25, 50, 100].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <span className="text-xs text-ink-500">
                Showing {page * rowsPerPage + 1} - {Math.min((page + 1) * rowsPerPage, totalCustomers)} of {totalCustomers}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(0)}
                  disabled={page === 0}
                >
                  First
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage((prev) => (prev + 1) * rowsPerPage < totalCustomers ? prev + 1 : prev)}
                  disabled={(page + 1) * rowsPerPage >= totalCustomers}
                >
                  Next
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(Math.floor((totalCustomers - 1) / rowsPerPage))}
                  disabled={(page + 1) * rowsPerPage >= totalCustomers}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transaction History Modal */}
      {historyDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl border border-border-light shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-light">
              <h3 className="text-sm font-black uppercase tracking-wider text-ink-900">
                Transaction History - {selectedCustomer?.name} ({selectedCustomer?.customerUniqueId})
              </h3>
              <button
                onClick={() => setHistoryDialogOpen(false)}
                className="text-ink-400 hover:text-ink-600 transition-colors p-1.5 rounded-lg hover:bg-canvas-muted"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-grow">
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <LogoLoader size="md" />
                </div>
              ) : (
                <Table>
                  <Table.Head>
                    <tr>
                      <Table.HeadCell align="center">Date</Table.HeadCell>
                      <Table.HeadCell align="center">Type</Table.HeadCell>
                      <Table.HeadCell align="right">Amount</Table.HeadCell>
                      <Table.HeadCell align="center">Cash Type</Table.HeadCell>
                      <Table.HeadCell align="left">Narration</Table.HeadCell>
                    </tr>
                  </Table.Head>
                  <Table.Body>
                    {customerHistory.map((txn, idx) => (
                      <Table.Row key={idx}>
                        <Table.Cell align="center">{formatDate(txn.date)}</Table.Cell>
                        <Table.Cell align="center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                            txn.transactionType === 'deposit'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-rose-50 text-rose-700 border border-rose-200'
                          }`}>
                            {txn.transactionType}
                          </span>
                        </Table.Cell>
                        <Table.Cell align="right" className="font-semibold text-ink-800">
                          ₹{Number(txn.amount).toFixed(2)}
                        </Table.Cell>
                        <Table.Cell align="center" className="text-ink-600 font-medium">{txn.cash_type}</Table.Cell>
                        <Table.Cell align="left" className="max-w-[200px] truncate">{txn.narration || '-'}</Table.Cell>
                      </Table.Row>
                    ))}
                    {customerHistory.length === 0 && (
                      <Table.EmptyRow colSpan={5} message="No history found" />
                    )}
                  </Table.Body>
                </Table>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-canvas-muted border-t border-border-light flex justify-end">
              <Button variant="ghost" onClick={() => setHistoryDialogOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default SavingsReport;